/**
 * InnPanel -- Rest and heal at the inn.
 *
 * Shows before/after HP comparison and heals all party members.
 * Cost: 10g per party member.
 *
 * @module InnPanel
 */

import focusManager from '../../input/FocusManager.js';
import townSystem from '../../systems/TownSystem.js';

export default class InnPanel {
  constructor(options = {}) {
    this._party = options.party || null;
    this._townId = options.townId || null;
    this._onClose = options.onClose || null;
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._healResult = null;
  }

  get isOpen() { return this._isOpen; }

  open() {
    if (this._isOpen) return;
    const uiRoot = document.getElementById('ui-root');
    if (!uiRoot) return;

    this.close();
    this._isOpen = true;

    this._el = document.createElement('div');
    this._el.className = 'town-panel-overlay';
    this._el.id = 'inn-panel';
    this._el.setAttribute('role', 'dialog');
    this._el.setAttribute('aria-label', 'Inn');
    this._el.setAttribute('data-scene', 'inn');

    this._render();
    uiRoot.appendChild(this._el);
    focusManager.pushGroup('inn', this._el);
  }

  close() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
      const topGroup = focusManager._currentGroup ? focusManager._currentGroup() : null;
      if (topGroup && topGroup.name === 'inn') {
        focusManager.popGroup();
      }
    }
    this._el = null;
    this._isOpen = false;
    this._statusMsg = '';
    this._healResult = null;
    if (this._onClose) this._onClose();
  }

  _render() {
    if (!this._el) return;
    this._el.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'town-panel';

    // Header
    const header = document.createElement('div');
    header.className = 'town-panel-header';
    const title = document.createElement('h2');
    title.className = 'town-panel-title';
    title.textContent = 'Inn';
    header.appendChild(title);

    const gold = document.createElement('span');
    gold.className = 'town-panel-gold';
    gold.textContent = `Gold: ${this._party?.gold || 0}`;
    header.appendChild(gold);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'town-panel-close';
    closeBtn.setAttribute('data-focusable', 'true');
    closeBtn.textContent = 'X';
    closeBtn.addEventListener('click', () => this.close());
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Status
    if (this._statusMsg) {
      const status = document.createElement('div');
      status.className = 'town-panel-status';
      status.textContent = this._statusMsg;
      panel.appendChild(status);
    }

    const content = document.createElement('div');
    content.className = 'town-panel-content';

    const party = this._party;
    if (!party || party.members.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'town-panel-empty';
      empty.textContent = 'No party members to heal.';
      content.appendChild(empty);
    } else if (this._healResult) {
      this._renderHealResult(content);
    } else {
      this._renderHealPreview(content);
    }

    panel.appendChild(content);
    this._el.appendChild(panel);
  }

  _renderHealPreview(container) {
    const party = this._party;
    const cost = townSystem.getInnCost(party);
    const canAfford = party.gold >= cost;

    const desc = document.createElement('div');
    desc.className = 'town-panel-desc';
    desc.textContent = `Rest at the inn to heal all party members. Cost: ${cost}g`;
    container.appendChild(desc);

    // Show current party HP
    const grid = document.createElement('div');
    grid.className = 'town-inn-grid';

    for (const member of party.members) {
      const row = document.createElement('div');
      row.className = 'town-inn-member';

      const name = document.createElement('span');
      name.className = 'town-inn-name';
      name.textContent = member.name;
      row.appendChild(name);

      const hp = document.createElement('span');
      hp.className = 'town-inn-hp';
      const hpPct = Math.round((member.hp / member.maxHp) * 100);
      hp.textContent = `HP: ${member.hp}/${member.maxHp} (${hpPct}%)`;
      if (hpPct < 50) hp.classList.add('town-inn-hp--low');
      else if (hpPct < 100) hp.classList.add('town-inn-hp--mid');
      else hp.classList.add('town-inn-hp--full');
      row.appendChild(hp);

      const mana = document.createElement('span');
      mana.className = 'town-inn-mana';
      mana.textContent = `Mana: ${member.mana}/${member.maxMana}`;
      row.appendChild(mana);

      grid.appendChild(row);
    }

    container.appendChild(grid);

    // Rest button
    const restBtn = document.createElement('button');
    restBtn.className = `town-panel-btn town-inn-rest-btn${!canAfford ? ' town-panel-btn--disabled' : ''}`;
    restBtn.id = 'inn-rest-btn';
    restBtn.setAttribute('data-focusable', 'true');
    restBtn.textContent = `Rest (${cost}g)`;

    if (canAfford) {
      restBtn.addEventListener('click', () => {
        const result = townSystem.restAtInn(party);
        this._statusMsg = result.message;
        this._healResult = result.healed;
        this._render();
        focusManager.refreshGroup();
      });
    }

    container.appendChild(restBtn);
  }

  _renderHealResult(container) {
    const desc = document.createElement('div');
    desc.className = 'town-panel-desc town-inn-healed';
    desc.textContent = 'Your party has been fully restored!';
    container.appendChild(desc);

    const grid = document.createElement('div');
    grid.className = 'town-inn-grid';

    for (const entry of this._healResult) {
      const row = document.createElement('div');
      row.className = 'town-inn-member';

      const name = document.createElement('span');
      name.className = 'town-inn-name';
      name.textContent = entry.name;
      row.appendChild(name);

      const hp = document.createElement('span');
      hp.className = 'town-inn-hp town-inn-hp--full';
      hp.textContent = `HP: ${entry.hpBefore} -> ${entry.hpAfter}`;
      row.appendChild(hp);

      const mana = document.createElement('span');
      mana.className = 'town-inn-mana';
      mana.textContent = `Mana: ${entry.manaBefore} -> ${entry.manaAfter}`;
      row.appendChild(mana);

      grid.appendChild(row);
    }

    container.appendChild(grid);

    // Done button
    const doneBtn = document.createElement('button');
    doneBtn.className = 'town-panel-btn';
    doneBtn.setAttribute('data-focusable', 'true');
    doneBtn.textContent = 'Done';
    doneBtn.addEventListener('click', () => {
      this._healResult = null;
      this._statusMsg = '';
      this._render();
      focusManager.refreshGroup();
    });
    container.appendChild(doneBtn);
  }
}
